<script setup lang="ts">
import { BaseButton } from '@/components/base'
import { useNavigator } from '@/hooks'
import { navigationRoutes } from '@/router/routes'
import { routeAssets } from '@/router/routes-assets'
import {
  APP_NAME,
  APP_ENV,
  COLLAPSED_NAVBAR_WIDTH,
  NAVBAR_WIDTH,
  APP_VERSION
} from '@/utils/constants'
import { computed, onMounted, ref } from 'vue'
import type { RouteRecordRaw } from 'vue-router'
import { VList, VListItem, VNavigationDrawer } from 'vuetify/components'

const isNavbarCollapsed = defineModel({ default: true })
const navigator = useNavigator()

interface NestedMenuState {
  isVisible: boolean
  isCollapsedVisible: boolean
  position?: DOMRect
}

const nestedMenuStates = ref<Record<string, NestedMenuState>>({})
const currentRoute = computed(() => navigator.getCurrentRoute())

onMounted(async () => {
  await navigator.getRouterInstance().isReady()
  const initPath = navigator.getCurrentRoute().path
  for (const route of navigationRoutes) {
    if (!route.children) continue
    for (const child of route.children) {
      if (initPath === child.path || initPath.startsWith(child.path + '/')) {
        nestedMenuStates.value[route.path] = { isVisible: true, isCollapsedVisible: false }
        return
      }
    }
  }
})
const getRouteAsset = (route: RouteRecordRaw) => routeAssets[route.path]

const isNestedRoute = (route: RouteRecordRaw): boolean => {
  return !!route.children && route.children.length > 0
}

const isActiveRoute = (route: RouteRecordRaw): boolean => {
  if (currentRoute.value.name === route.name) return true
  const currentPath = currentRoute.value.path
  const routePath = route.path
  return currentPath === routePath || currentPath.startsWith(routePath + '/')
}

const initializeNestedMenuState = (route: RouteRecordRaw) => {
  if (isNestedRoute(route) && !nestedMenuStates.value[route.path]) {
    nestedMenuStates.value[route.path] = {
      isVisible: false,
      isCollapsedVisible: false
    }
  }
}

const toggleNestedMenu = (route: RouteRecordRaw, event?: MouseEvent | KeyboardEvent) => {
  if (!isNestedRoute(route)) return

  initializeNestedMenuState(route)
  const position = event?.currentTarget as HTMLElement
  const state = nestedMenuStates.value[route.path]

  if (isNavbarCollapsed.value) {
    state.position = position.getBoundingClientRect()
    state.isCollapsedVisible = !state.isCollapsedVisible
  } else {
    state.isVisible = !state.isVisible
  }
}

const handleNavbarCollapse = () => {
  isNavbarCollapsed.value = !isNavbarCollapsed.value
  Object.values(nestedMenuStates.value).forEach((state) => {
    state.isCollapsedVisible = false
  })
}
</script>

<template>
  <v-navigation-drawer
    absolute
    class="navbar"
    color="slate_800"
    permanent
    :width="NAVBAR_WIDTH"
    :rail="isNavbarCollapsed"
    :rail-width="COLLAPSED_NAVBAR_WIDTH"
  >
    <template #prepend>
      <BaseButton
        size="32"
        height-variant="auto"
        class="collapse-button bg-slate_800"
        :class="isNavbarCollapsed ? 'collapsed' : ''"
        @click="handleNavbarCollapse"
      >
        <v-icon icon="mdi:mdi-chevron-left" size="24" :rotate-180="isNavbarCollapsed" />
      </BaseButton>
      <v-list-item height="88" class="navbar-logo pa-6">
        <span v-if="!isNavbarCollapsed">{{ APP_NAME }}</span>
      </v-list-item>
    </template>

    <v-list density="compact" nav class="px-0 d-flex flex-column ga-3">
      <template v-for="route in navigationRoutes" :key="route.path">
        <div
          class="d-flex flex-column"
          :class="{ 'bg-brand-color--light': nestedMenuStates[route.path]?.isVisible }"
        >
          <v-list-item
            class="pr-0 py-3 d-flex align-center mb-0 body-1 navbar--item rounded-lg"
            :class="isNavbarCollapsed ? 'mx-2 collapsed-navbar--items-padding' : 'mx-6 pl-4'"
            :to="isNestedRoute(route) ? undefined : route.path"
            :active="isNestedRoute(route) ? false : isActiveRoute(route)"
            active-class="bg-blue_600"
            @click="(e) => toggleNestedMenu(route, e)"
          >
            <v-img :src="getRouteAsset(route)" :size="24" width="20" max-width="20" />
            <span class="route-name__wrapper">
              {{ route.name }}
            </span>
            <template v-if="isNestedRoute(route)">
              <v-icon
                v-if="!isNavbarCollapsed"
                icon="mdi-chevron-down"
                size="24"
                class="chevron-icon-pos rotate-transition"
                :class="{ 'rotate-n180': nestedMenuStates[route.path]?.isVisible }"
              />
              <v-icon v-else icon="mdi-chevron-right" size="20" class="ml-1 rotate-transition" />
            </template>
          </v-list-item>

          <!-- Nested menu for collapsed navbar -->
          <Transition name="slide-x-transition">
            <Teleport to="body" v-if="isNavbarCollapsed && isNestedRoute(route)">
              <v-list
                v-if="nestedMenuStates[route.path]?.isCollapsedVisible"
                density="compact"
                nav
                class="pa-3 ml-1 nested-routes detached-nested-routes rounded"
                :style="{
                  top: `${nestedMenuStates[route.path]?.position?.top}px`,
                  left: `${COLLAPSED_NAVBAR_WIDTH}px`
                }"
                active-class="active-route"
                v-click-outside="() => (nestedMenuStates[route.path].isCollapsedVisible = false)"
              >
                <v-list-item
                  v-for="childRoute in route.children"
                  :key="childRoute.path"
                  class="px-3 d-flex align-center"
                  :to="childRoute.path"
                  :active="isActiveRoute(childRoute)"
                  @click="() => (nestedMenuStates[route.path].isCollapsedVisible = false)"
                >
                  <span class="nested-route text-white body-2">
                    {{ childRoute.name }}
                  </span>
                </v-list-item>
              </v-list>
            </Teleport>
          </Transition>

          <!-- Nested menu for expanded navbar -->
          <Transition name="slide-y-transition">
            <v-list
              v-if="!isNavbarCollapsed && nestedMenuStates[route.path]?.isVisible"
              density="compact"
              nav
              class="px-4 ml-12 nested-routes"
            >
              <v-list-item
                v-for="childRoute in route.children"
                :key="childRoute.path"
                class="px-3 d-flex align-center"
                :to="childRoute.path"
                :active="isActiveRoute(childRoute)"
                active-class="bg-blue_600"
              >
                <span class="nested-route body-2">
                  {{ childRoute.name }}
                </span>
              </v-list-item>
            </v-list>
          </Transition>
        </div>
      </template>
    </v-list>
    <template #append v-if="!isNavbarCollapsed">
      <div
        class="d-flex flex-column ga-2 px-6 py-3 text-text_primary small body-2 env-info align-start"
      >
        <span v-if="APP_ENV != 'Production'" class="bg-warning_700 body-3 px-1 rounded">{{
          APP_ENV
        }}</span>
        <span class="text-slate_400">Version: {{ APP_VERSION }}</span>
      </div>
    </template>
  </v-navigation-drawer>
</template>

<style scoped lang="scss">
.navbar-logo {
  font-size: 24px;
  line-height: 29.05px;
  font-weight: 900;
  letter-spacing: 2%;
}

.collapse-button {
  border: 1px solid #4a5b6b;
  border-radius: 50%;
  position: absolute;
  right: 0;
  top: calc(0.5 * 88px);
  transform: translate(50%, -50%);
  z-index: 1;

  .v-icon[rotate-180='true'] {
    transition: transform 0.6s cubic-bezier(0.075, 0.82, 0.165, 1);
    transform: rotate(180deg);
  }

  &.collapsed {
    transform: translate(-60%, -50%);
  }
}

.env-info {
  bottom: 40px;
  border-radius: 4px;
}

.copyright-info,
.navbar-logo,
.env-info {
  text-wrap: nowrap;
}

.route-name__wrapper,
.nested-routes {
  white-space: nowrap;
}

.v-navigation-drawer--rail .route-name__wrapper {
  &:not(.nested-route) {
    opacity: 0;
  }
  &.nested-route {
    position: unset;
  }
}

.nested-routes:not(.detached-nested-routes)::before {
  content: '';
  display: block;
  height: 88%;
  position: absolute;
  left: 0;
  border-left: 1px solid rgb(var(--v-theme-gray_400));
}

.detached-nested-routes {
  position: absolute;
  left: 0;
  top: 0;
  background-color: rgb(var(--v-theme-brand_dark)) !important;
  z-index: 1;

  .active-route {
    background-color: rgb(var(--v-theme-blue_600));
  }
}

.rotate-n180 {
  transform: rotate(-180deg);
}

.rotate-transition {
  transition: transform 0.3s ease;
}

.chevron-icon-pos {
  margin-left: 0;
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
}

.navbar--item {
  transition: all 0.3s cubic-bezier(0.52, 0.49, 0, 1.09);
}
.collapsed-navbar--items-padding {
  padding-inline: 18px;
}
</style>
